import type { NexusNavItem } from '../types';

export type BreadcrumbCrumb = {
    label: string;
    href?: string | null;
};

export function normalizePath(url: string | null | undefined): string {
    if (!url) {
        return '';
    }

    let pathname = url;
    try {
        pathname = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost').pathname;
    } catch {
        pathname = url.split('?')[0]?.split('#')[0] ?? url;
    }

    if (pathname.length > 1 && pathname.endsWith('/')) {
        return pathname.slice(0, -1);
    }

    return pathname || '/';
}

export function pathMatches(href: string | null | undefined, path: string): boolean {
    if (!href || href.startsWith('javascript:') || href === '#') {
        return false;
    }

    const clean = normalizePath(href);
    const current = normalizePath(path);

    if (!clean || clean === '#') {
        return false;
    }

    if (clean === current) {
        return true;
    }

    if (clean !== '/' && current.startsWith(`${clean}/`)) {
        return true;
    }

    return false;
}

export function isTrackablePath(path: string): boolean {
    const clean = normalizePath(path);
    if (!clean || clean === '#' || clean.startsWith('javascript:')) {
        return false;
    }

    // Apenas rotas do shell Nexus
    return clean === '/nexus' || clean.startsWith('/nexus/');
}

/** Resolve trilha Home → grupo → item a partir do menu compartilhado. */
export function resolveMenuTrail(
    menu: NexusNavItem[],
    path: string,
): BreadcrumbCrumb[] {
    const current = normalizePath(path);
    let best: {
        score: number;
        group: NexusNavItem;
        childLabel?: string;
        childHref?: string;
    } | null = null;

    for (const item of menu) {
        if (item.children?.length) {
            for (const child of item.children) {
                if (!pathMatches(child.href, current)) {
                    continue;
                }
                const childPath = normalizePath(child.href);
                const score = childPath.length;
                if (!best || score > best.score) {
                    best = {
                        score,
                        group: item,
                        childLabel: child.label,
                        childHref: child.href,
                    };
                }
            }
        }

        if (item.href && pathMatches(item.href, current)) {
            const itemPath = normalizePath(item.href);
            const score = itemPath.length;
            if (!best || score > best.score) {
                best = { score, group: item, childLabel: undefined, childHref: item.href };
            }
        }
    }

    if (!best) {
        return [];
    }

    const crumbs: BreadcrumbCrumb[] = [
        { label: best.group.label, href: best.group.href ?? null },
    ];

    if (best.childLabel) {
        crumbs.push({ label: best.childLabel, href: best.childHref ?? null });
    }

    return crumbs;
}

export function resolvePageTitleFromMenu(
    menu: NexusNavItem[],
    path: string,
): string | null {
    const trail = resolveMenuTrail(menu, path);
    if (trail.length === 0) {
        return null;
    }
    return trail[trail.length - 1]?.label ?? null;
}

export function friendlyPathTitle(path: string): string {
    const clean = normalizePath(path);
    if (clean === '/nexus' || clean === '/') {
        return 'Painel';
    }

    const segment = clean.split('/').filter(Boolean).pop() ?? clean;
    return segment
        .replace(/[-_]+/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function resolvePageTitle(options: {
    menu: NexusNavItem[];
    path: string;
    pageTitle?: string | null;
    documentTitle?: string | null;
}): string {
    const fromMenu = resolvePageTitleFromMenu(options.menu, options.path);
    if (fromMenu) {
        return fromMenu;
    }

    if (options.pageTitle?.trim()) {
        return options.pageTitle.trim();
    }

    const doc = options.documentTitle?.trim();
    if (doc) {
        const stripped = doc.replace(/\s*[·|–-]\s*Infor\s*Erp\s*web$/i, '').trim();
        if (stripped) {
            return stripped;
        }
    }

    return friendlyPathTitle(options.path);
}

export function buildBreadcrumbs(options: {
    menu: NexusNavItem[];
    path: string;
    pageTitle?: string | null;
    homeLabel?: string;
    homeHref?: string;
}): BreadcrumbCrumb[] {
    const homeLabel = options.homeLabel ?? 'SNGPC WEB INFOR';
    const homeHref = options.homeHref ?? '/nexus';
    const current = normalizePath(options.path);

    const home: BreadcrumbCrumb = { label: homeLabel, href: homeHref };
    const trail = resolveMenuTrail(options.menu, current);

    if (trail.length > 0) {
        return [home, ...trail];
    }

    const title = resolvePageTitle({
        menu: options.menu,
        path: current,
        pageTitle: options.pageTitle,
        documentTitle: typeof document !== 'undefined' ? document.title : null,
    });

    if (current === normalizePath(homeHref)) {
        return [home, { label: title, href: null }];
    }

    return [home, { label: title, href: null }];
}
